import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Typography, Button, Alert, CircularProgress, IconButton,
} from "@mui/material";
import { ArrowBack, Shield } from "@mui/icons-material";
import { gqlRequest, VERIFY_AUTH_TOKEN } from "../../api/graphql";
import { useAuth } from "../../store/AuthContext";
import { COLORS } from "../../theme";

export default function VerifyPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputs = useRef([]);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...code];
    next[i] = val;
    setCode(next);
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (code[i]) {
        const next = [...code]; next[i] = ""; setCode(next);
      } else if (i > 0) {
        inputs.current[i - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length) {
      setCode([...pasted.padEnd(6, "").split("").slice(0, 6)]);
      inputs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join("");
    if (fullCode.length < 6) { setError("Please enter the full 6-digit code."); return; }

    setLoading(true); setError("");
    try {
      // gqlRequest reads token from localStorage automatically —
      // the temp token was stored there during adminSignIn.
      const data = await gqlRequest(VERIFY_AUTH_TOKEN, { code: fullCode });
      const res = data?.verifytwofactorauth;

      if (!res?.token) {
        setError("Invalid verification code. Please try again.");
        return;
      }

      // Replace temp token with the fully verified session token
      login(res.token, { lastlogin: res.lastlogin });
      navigate("/businesses");
    } catch (e) {
      setError(e.message || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Clear the temp token and go back to login
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: COLORS.surface,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <Paper sx={{ width: 440, p: 5, borderRadius: 3, border: `1px solid ${COLORS.border}`, boxShadow: "0 4px 32px rgba(79,70,229,.08)" }}>
        <IconButton onClick={handleBack} sx={{ mb: 2, ml: -1, border: `1px solid ${COLORS.border}`, borderRadius: 1.5 }}>
          <ArrowBack fontSize="small" />
        </IconButton>

        {/* Icon */}
        <Box sx={{
          width: 52, height: 52, borderRadius: 2, mb: 3,
          bgcolor: COLORS.accentLight, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Shield sx={{ color: COLORS.accent, fontSize: 28 }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Two-factor verification</Typography>
        <Typography variant="body2" sx={{ color: COLORS.muted, mb: 3.5 }}>
          Enter the 6-digit code sent to your email
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* OTP boxes */}
        <Box sx={{ display: "flex", gap: 1.2, mb: 3.5, justifyContent: "center" }} onPaste={handlePaste}>
          {code.map((digit, i) => (
            <Box
              key={i}
              component="input"
              ref={el => inputs.current[i] = el}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              onFocus={e => e.target.select()}
              maxLength={1}
              inputMode="numeric"
              sx={{
                width: 52, height: 60,
                textAlign: "center", fontSize: 24, fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                border: `2px solid ${digit ? COLORS.accent : COLORS.border}`,
                borderRadius: "10px",
                outline: "none",
                background: digit ? COLORS.accentLight : COLORS.card,
                color: COLORS.accent,
                cursor: "text",
                transition: "all 0.15s",
                "&:focus": { borderColor: COLORS.accent, background: COLORS.accentLight },
              }}
            />
          ))}
        </Box>

        <Button
          fullWidth variant="contained"
          onClick={handleVerify}
          disabled={loading || code.join("").length < 6}
          sx={{
            py: 1.4, borderRadius: 2, fontWeight: 700, fontSize: 15,
            bgcolor: COLORS.accent, "&:hover": { bgcolor: "#4338ca" },
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Verify & Continue"}
        </Button>

        <Typography variant="body2" sx={{ textAlign: "center", mt: 2.5, color: COLORS.muted }}>
          Wrong account?{" "}
          <Typography
            component="span"
            onClick={handleBack}
            sx={{ color: COLORS.accent, fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          >
            Sign in with another account
          </Typography>
        </Typography>
      </Paper>
    </Box>
  );
}
